import React, { useState } from 'react';
import Cluster from '@urbica/react-map-gl-cluster';
import { Marker, Popup } from '@urbica/react-map-gl';
import { Map } from '../map';
import { colors } from '../../utils/theme';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import Image from 'next/image';
import getAmplitude from '../../utils/amplitude';

const ClusterMarker = React.memo<{ latitude: number; longitude: number; pointCount: number }>(
  ({ longitude, latitude, pointCount }) => {
    return (
      <Marker longitude={longitude} latitude={latitude}>
        <div
          style={{
            width: pointCount > 99 ? '30px' : pointCount > 9 ? '25px' : '20px',
            height: pointCount > 99 ? '30px' : pointCount > 9 ? '25px' : '20px',
            color: '#fff',
            background: '#1978c8',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            backgroundColor: colors.lightGreen,
          }}>
          <p style={{ height: 18 }}>{pointCount}</p>
        </div>
      </Marker>
    );
  },
  (prev, next) => {
    return prev.pointCount === next.pointCount;
  },
);

type Props = { viewport: any; geojson: any; style?: any };

const MapListings = React.memo<Props>(({ viewport, geojson, style }) => {
  const router = useRouter();
  const { slug } = router.query;
  const [hover, setHover] = useState<any>();
  const shouldShow = geojson?.features && geojson?.features.length > 0;
  return (
    <Map viewport={viewport} style={{ width: '100%', height: '100%', ...style }}>
      {shouldShow && (
        <Cluster radius={40} extent={512} nodeSize={64} component={ClusterMarker}>
          {geojson.features.map(point => (
            <Marker key={point.properties.id} longitude={point.geometry.coordinates[0]} latitude={point.geometry.coordinates[1]}>
              <motion.div
                onHoverStart={() => {
                  setHover(point);
                }}
                onHoverEnd={() => {
                  setHover(undefined);
                }}
                onClick={async () => {
                  await router.push(`/${slug}/${point.properties.type}/${point.properties.slug}`);
                  window.scrollTo({ top: 0 });
                  const amplitude = await getAmplitude();
                  amplitude.logEvent('Clicked on listing map circle', { app: point.properties.slug, itemType: point.properties.itemType });
                }}
                initial="rest"
                whileHover="hover"
                animate="rest"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <motion.div
                  variants={{
                    hover: {
                      scale: 1.5,
                    },
                    rest: {
                      scale: 1,
                    },
                  }}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 40,
                    border: `2px solid ${colors.lightGreen}`,
                  }}>
                  <Image className="mapImage" width={150} height={150} src={point.properties.image} alt={point.properties.title} />
                </motion.div>
              </motion.div>
            </Marker>
          ))}
        </Cluster>
      )}
      {hover && (
        <Popup longitude={hover.geometry.coordinates[0]} latitude={hover.geometry.coordinates[1]} anchor={'bottom'} closeButton={false} offset={40}>
          {hover.properties.title}
        </Popup>
      )}
    </Map>
  );
});

export default MapListings;
